import { useState } from "react";
import { Box, Typography, Tooltip, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FolderIcon from "@mui/icons-material/Folder";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PeopleIcon from "@mui/icons-material/People";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BadgeIcon from "@mui/icons-material/Badge";
import InventoryIcon from "@mui/icons-material/Inventory";
import StoreIcon from "@mui/icons-material/Store";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import BusinessIcon from "@mui/icons-material/Business";

/* ── Nav item ── */
const NavItem = ({ icon, label, path, isCollapsed, navigate, location }) => {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const white = theme.palette.secondary.white;
  const isActive = location.pathname.includes(path);

  const inner = (
    <Box
      onClick={() => navigate(`/${path}`)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: isCollapsed ? 0 : "1rem",
        py: "9px",
        mx: isCollapsed ? "auto" : "0.5rem",
        borderRadius: "8px",
        cursor: "pointer",
        justifyContent: isCollapsed ? "center" : "flex-start",
        width: isCollapsed ? "40px" : "auto",
        height: isCollapsed ? "40px" : "auto",
        backgroundColor: isActive ? "rgba(29,158,117,0.15)" : "transparent",
        color: isActive ? accent : "rgba(255,255,255,0.55)",
        position: "relative",
        transition: "all 0.15s",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.06)",
          color: white,
        },
        ...(isActive && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: "-8px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "3px",
            height: "60%",
            borderRadius: "0 3px 3px 0",
            backgroundColor: accent,
          },
        }),
      }}
    >
      <Box sx={{ fontSize: "20px", display: "flex", flexShrink: 0, color: isActive ? accent : "inherit" }}>
        {icon}
      </Box>
      {!isCollapsed && (
        <Typography sx={{ fontSize: "13px", fontWeight: isActive ? 500 : 400, whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      )}
    </Box>
  );

  return isCollapsed ? (
    <Tooltip title={label} placement="right" arrow
      slotProps={{ popper: { sx: { [`& .MuiTooltip-tooltip`]: { marginLeft: "0px !important" } } } }}
    >
      {inner}
    </Tooltip>
  ) : inner;
};

/* ── Section label ── */
const SectionLabel = ({ label, isCollapsed }) =>
  isCollapsed ? (
    <Box sx={{ height: "0.5px", backgroundColor: "rgba(255,255,255,0.08)", mx: "0.75rem", my: "0.5rem" }} />
  ) : (
    <Typography sx={{
      fontSize: "10px", fontWeight: 500,
      letterSpacing: "0.1em", textTransform: "uppercase",
      color: "rgba(255,255,255,0.25)",
      px: "1.5rem", pt: "1.25rem", pb: "0.4rem",
    }}>
      {label}
    </Typography>
  );

/* ── Sidebar ── */
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const modules = useSelector((state) => state.authReducer.modules);

  const [isCollapsed, setIsCollapsed] = useState(true);

  const bg = theme.palette.background.default;
  const accent = theme.palette.secondary.main;
  const white = theme.palette.secondary.white;

  const navProps = { isCollapsed, navigate, location };

  const inventoryItems = [
    { icon: <ReceiptIcon fontSize="inherit" />, label: "Purchases", path: "purchase" },
    { icon: <InventoryIcon fontSize="inherit" />, label: "Inventory", path: "inventory" },
    // { icon: <AssignmentReturnIcon fontSize="inherit" />, label: "Stock Transfers", path: "transfers" },
    { icon: <FolderIcon fontSize="inherit" />, label: "Setup Inventory", path: "setup" },
    // { icon: <LocalShippingIcon fontSize="inherit" />, label: "Suppliers", path: "supplier" },
  ];

  const adminItems = [
    { icon: <AssessmentIcon fontSize="inherit" />, label: "Reporting", path: "reports" },
    { icon: <ManageAccountsIcon fontSize="inherit" />, label: "User Management", path: "userManagement" },
    { icon: <BusinessIcon fontSize="inherit" />, label: "Business", path: "business" },
    { icon: <TuneIcon fontSize="inherit" />, label: "Settings", path: "settings" },
  ];

  return (
    <Box
      sx={{
        width: isCollapsed ? "64px" : "220px",
        minWidth: isCollapsed ? "64px" : "220px",
        height: "100vh",
        backgroundColor: bg,
        borderRight: "1px solid rgba(29,158,117,0.12)",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflowX: "hidden",
        overflowY: "auto",
        zIndex: 50,
        flexShrink: 0,
      }}
    >
      {/* ── Toggle header ── */}
      <Box
        sx={{
          height: "56px",
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-end",
          px: isCollapsed ? 0 : "0.75rem",
          borderBottom: "1px solid rgba(29,158,117,0.12)",
          flexShrink: 0,
        }}
      >
        <Tooltip title={isCollapsed ? "Expand" : "Collapse"} placement="right">
          <Box
            onClick={() => setIsCollapsed(!isCollapsed)}
            sx={{
              width: "32px", height: "32px",
              borderRadius: "8px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
              color: "rgba(255,255,255,0.4)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.07)", color: white },
              transition: "all 0.15s",
            }}
          >
            <MenuOutlinedIcon sx={{ fontSize: "20px" }} />
          </Box>
        </Tooltip>
      </Box>

      {/* ── Nav ── */}
      <Box sx={{ flex: 1, py: "0.75rem", display: "flex", flexDirection: "column", gap: "2px" }}>

        <SectionLabel label="Core" isCollapsed={isCollapsed} />
        <NavItem icon={<DashboardIcon fontSize="inherit" />} label="Dashboard" path="dashboards" {...navProps} />

        {modules.billing === 1 && (
          <NavItem icon={<StoreIcon fontSize="inherit" />} label="Sales" path="billing" {...navProps} />
        )}

        {modules.inventory === 1 && (
          <>
            <SectionLabel label="Inventory" isCollapsed={isCollapsed} />
            {inventoryItems.map((item) => (
              <NavItem key={item.path} {...item} {...navProps} />
            ))}
          </>
        )}

        {(modules.customers === 1 || modules.accounting === 1 || modules.human_resource === 1) && (
          <SectionLabel label="Management" isCollapsed={isCollapsed} />
        )}
        {modules.customers === 1 && (
          <NavItem icon={<PeopleIcon fontSize="inherit" />} label="Customers" path="customer" {...navProps} />
        )}
        {modules.accounting === 1 && (
          <NavItem icon={<AccountBalanceIcon fontSize="inherit" />} label="Accounting" path="accounting" {...navProps} />
        )}
        {modules.human_resource === 1 && (
          <NavItem icon={<BadgeIcon fontSize="inherit" />} label="Human Resource" path="humanResource" {...navProps} />
        )}
        {modules.uploads === 1 && (
          <NavItem icon={<DriveFolderUploadIcon fontSize="inherit" />} label="File Uploads" path="fileUploads" {...navProps} />
        )}

        {modules.admin === 1 && (
          <>
            <SectionLabel label="Admin" isCollapsed={isCollapsed} />
            {adminItems.map((item) => (
              <NavItem key={item.path} {...item} {...navProps} />
            ))}
          </>
        )}
      </Box>

      {/* ── Footer ── */}
      {!isCollapsed && (
        <Box sx={{
          px: "1.25rem", py: "1rem",
          borderTop: "1px solid rgba(29,158,117,0.1)",
          flexShrink: 0,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: accent }} />
            <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.2)", letterSpacing: "0.06em" }}>
              PESATRACK
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;